import { redirect } from '@tanstack/react-router'
import { bootstrapQueryOptions } from '@/features/bootstrap/api/bootstrap/queries/bootstrap-query'
import { requireAuthenticated } from '@/shared/lib/route-guards'
import { setLastWorkspaceId } from '@/shared/lib/last-workspace'

/** GTM screens: signed in, and GTM hired in this workspace. */
export async function requireGtmWorkspace(
  context: Parameters<typeof requireAuthenticated>[0],
  params: { lang: string; workspaceId: string },
) {
  requireAuthenticated(context, params)
  setLastWorkspaceId(params.workspaceId)
  const bootstrap = await context.queryClient.ensureQueryData(
    bootstrapQueryOptions(),
  )
  const workspace = bootstrap.workspaces.find(
    (item) => item.id === params.workspaceId,
  )
  if (!workspace?.employees.includes('gtm')) {
    throw redirect({
      to: '/$lang/workspace/$workspaceId',
      params: { lang: params.lang, workspaceId: params.workspaceId },
      replace: true,
    })
  }
}

/**
 * Team-product pages hidden while Laplace x GTM is single-user (see
 * GTM_PRODUCT_SCOPE): old links land on the user's GTM entry instead.
 * Authentication still applies first.
 */
export function redirectToGtmEntry(
  context: Parameters<typeof requireAuthenticated>[0],
  params: { lang: string },
): never {
  requireAuthenticated(context, params)
  throw redirect({
    to: '/$lang/app',
    params: { lang: params.lang },
    replace: true,
  })
}

/** Workspace chat/settings links open that workspace's GTM, when it has one. */
export async function redirectWorkspaceToGtm(
  context: Parameters<typeof requireAuthenticated>[0],
  params: { lang: string; workspaceId: string },
  section: 'analysis' | 'settings' = 'analysis',
): Promise<never> {
  requireAuthenticated(context, params)
  const bootstrap = await context.queryClient.ensureQueryData(
    bootstrapQueryOptions(),
  )
  const workspace = bootstrap.workspaces.find(
    (item) => item.id === params.workspaceId,
  )
  if (workspace?.employees.includes('gtm')) {
    throw redirect({
      to:
        section === 'settings'
          ? '/$lang/workspace/$workspaceId/gtm/settings'
          : '/$lang/workspace/$workspaceId/gtm',
      params: { lang: params.lang, workspaceId: params.workspaceId },
      replace: true,
    })
  }
  return redirectToGtmEntry(context, params)
}
